import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketDetail, TicketSpalte, Unternehmen } from "@kundeninfo/api-client";
import { Arbeitsdialog, Feld, Icon, darfAbbrechen, schnellknopfDatum, istFreierTag, useFormularZustand, useTelefon }
  from "@kundeninfo/ui";
import type { Schnellknopf } from "@kundeninfo/ui";
import { Auswahl, Chip, Eingabe, Formular, Hinweis, Register, Reiter, Steckbrief } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { datumsMarke, heuteBeschriftung, alleFeiertage, berlinerTag } from "./ticketFormat.js";
import { VerlaufReiter, TeilaufgabenReiter, KundenReiter, AnhaengeReiter } from "./TicketDialogTeile.js";
import { Avatar } from "./Avatar.js";
import { ART } from "./TicketKarte.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DER ARBEITSDIALOG EINES TICKETS (Spec „Arbeitsdialog des Tickets", F41): `breite="voll"`,
 * Pfad über dem Steckbrief, Kreuz immer sichtbar. Speichern/Abbrechen im Fuß gilt nur für Titel,
 * Beschreibung, Zuständig, Spalte und Wiedervorlage („Kopf“); Kommentare, Teilaufgaben, Kunden,
 * Bezüge und Anhänge sind sofort gespeichert (`useSchreibweg`, die Ticket-Version bleibt dabei
 * stehen, Aufgabe 7).
 *
 * 409 beim Speichern (Review-Fokus 2): Der Dialog holt den Serverstand sofort nach (Steckbrief
 * und Verlauf zeigen ihn), die EIGENEN Kopfänderungen bleiben stehen, das Formular steht im
 * „fremden Stand“ mit „Das Ticket wurde inzwischen geändert“. „Neu laden“ übernimmt den Stand
 * ganz, „Meine Eingaben behalten“ behält die eigenen Änderungen auf dem neuen Stand — kein
 * stiller Überschreiber.
 *
 * Startversion (Review 15, Lost Update): Solange der Kopf ungespeicherte Änderungen hat, bleibt
 * die Version, auf der die Bearbeitung begann (`basis`), stehen — auch wenn eine Unterobjekt-
 * Antwort oder ein Nachladen ein neueres Detail bringt. Gespeichert wird mit DIESER Version;
 * hat jemand anders den Kopf inzwischen gespeichert, antwortet der Server mit 409 und der
 * Merge-Weg oben greift. Ohne Kopfänderungen folgt die Basis dem Server.
 *
 * Die Entwürfe „Neuer Kommentar“ und „Neue Teilaufgabe“ leben hier (nicht im Reiter): Sie
 * überstehen den Reiterwechsel und zählen für die Rückfrage beim Abbrechen mit.
 *
 * Die Schnellknöpfe rücken die Wiedervorlage nur in der Oberfläche auf den Werktag
 * (`kalenderHilfe`); PATCH speichert das Datum wie gesendet, Wochenende/Feiertag ist nur ein
 * Hinweis (Gesamtreview M4; das Rücken durch den Server macht nur die Ablage).
 */

type Kopf = { title: string; descriptionMarkdown: string; assigneeUserId: string; columnId: string; resubmitDate: string };
const KOPF_FELDER = ["title", "descriptionMarkdown", "assigneeUserId", "columnId", "resubmitDate"] as const;

function kopfAus(d: TicketDetail): Kopf {
  return { title: d.title, descriptionMarkdown: d.descriptionMarkdown ?? "", assigneeUserId: d.assignee?.id ?? "",
           columnId: d.columnId, resubmitDate: d.resubmitDate ?? "" };
}

/** Eigene Änderungen (gegenüber `basis`) auf einen neuen Serverstand legen: was der Benutzer
 *  angefasst hat, bleibt; alles andere folgt dem Server. */
function zusammenfuehren(basis: Kopf, eigen: Kopf, neu: Kopf): Kopf {
  const ergebnis = { ...neu };
  for (const f of KOPF_FELDER) if (eigen[f] !== basis[f]) ergebnis[f] = eigen[f];
  return ergebnis;
}

type Stand = { d: TicketDetail; kopf: Kopf; basis: { version: number; kopf: Kopf } };
const istGeaendert = (s: Stand): boolean => KOPF_FELDER.some((f) => s.kopf[f] !== s.basis.kopf[f]);

/** Wie ein neuer Serverstand übernommen wird: `ersetzen` (Laden, „Neu laden“) verwirft die
 *  Kopfänderungen; `teile` (Unterobjekt-Antwort) lässt Kopf UND Startversion stehen, solange
 *  der Kopf geändert ist; `behalten` (409, „Meine Eingaben behalten“) legt die eigenen
 *  Änderungen auf den neuen Stand und rückt die Basis vor. */
type Uebernahme = "ersetzen" | "teile" | "behalten";

export type Entwuerfe = { kommentar: string; teilaufgabe: string };

export type TicketDialogProps = {
  client: TicketsClient; ticketId: string; companies: readonly Unternehmen[];
  ich: { id: string; istAdmin: boolean };
  onSchliessen: (geaendert: boolean) => void; onZurAkte: (customerId: string) => void;
  /** Der Pfad über dem Steckbrief — je Herkunft „Tickets › Board“ (Vorgabe) oder „Tickets › Liste“. */
  pfad?: string;
  /** Meldet, ob Ungespeichertes im Dialog steht (Kopfänderungen oder Entwürfe) — für den
   *  Verlassen-Wächter des Bereichs (Review 17). */
  onUngespeichert?: (ungespeichert: boolean) => void;
  zusatzReiter?: { id: string; label: string;
    inhalt: (d: TicketDetail, neu: (d: TicketDetail) => void, nachladen: () => Promise<TicketDetail>) => ReactNode }[];
};

export function TicketDialog(p: TicketDialogProps): ReactElement {
  const [stand, setStand] = useState<Stand | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Entwuerfe>({ kommentar: "", teilaufgabe: "" });
  const [spalten, setSpalten] = useState<TicketSpalte[]>([]);
  const [leute, setLeute] = useState<{ id: string; name: string; email: string | null }[]>([]);
  const [reiter, setReiter] = useState("verlauf");
  const [geaendertSeitOeffnen, setGeaendertSeitOeffnen] = useState(false);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const eingabenBehalten = useRef(false);
  const pfad = p.pfad ?? "Tickets › Board";
  // Am Telefon steht der Steckbrief anfangs eingeklappt (Sichtprobe 6): der Reiterinhalt ist ohne
  // Rollen im Bild, der Falt-Knopf des Elements zeigt die Kennwerte.
  const telefon = useTelefon();
  const [kopfZu, setKopfZu] = useState(telefon);

  const uebernehme = useCallback((neu: TicketDetail, wie: Uebernahme) => {
    setStand((s) => {
      const frisch: Stand = { d: neu, kopf: kopfAus(neu), basis: { version: neu.version, kopf: kopfAus(neu) } };
      if (s === null || wie === "ersetzen") return frisch;
      if (wie === "teile") return istGeaendert(s) ? { ...s, d: neu } : frisch;
      return { ...frisch, kopf: zusammenfuehren(s.basis.kopf, s.kopf, kopfAus(neu)) };
    });
  }, []);

  const holen = useCallback(() => p.client.getTicket(p.ticketId), [p.client, p.ticketId]);
  /** Für die Reiter (409 eines Unterobjekts): Die Antwort geht über `teileNeu`, also ohne Verlust
   *  der Kopfänderungen und ihrer Startversion. */
  const nachladen = holen;

  useEffect(() => {
    let aktiv = true;
    holen().then((neu) => { if (aktiv) { uebernehme(neu, "ersetzen"); setLadefehler(null); } })
      .catch((e: unknown) => {
        if (!aktiv) return;
        setLadefehler(e instanceof ApiError && e.status === 404 ? "Ticket nicht gefunden."
          : "Das Ticket konnte nicht geladen werden.");
      });
    return () => { aktiv = false; };
  }, [holen, uebernehme]);
  useEffect(() => {
    void p.client.listTicketColumns().then((r) => setSpalten(r.items)).catch(() => undefined);
    void p.client.listTicketAssignees().then((r) => setLeute(r.items)).catch(() => undefined);
  }, [p.client]);

  const d = stand?.d ?? null;
  const kopf = stand?.kopf ?? null;
  const geaendert = stand !== null && istGeaendert(stand);
  const entwurfOffen = entwuerfe.kommentar.trim() !== "" || entwuerfe.teilaufgabe.trim() !== "";
  const ungespeichert = geaendert || entwurfOffen;
  const onUngespeichert = p.onUngespeichert;
  useEffect(() => { onUngespeichert?.(ungespeichert); }, [onUngespeichert, ungespeichert]);
  const feiertage = useMemo(() => (d === null ? [] : alleFeiertage([d.kalender])), [d]);
  const aendereKopf = (teil: Partial<Kopf>) => setStand((s) => (s === null ? s : { ...s, kopf: { ...s.kopf, ...teil } }));

  const formular = useFormularZustand({
    speichern: async () => {
      if (stand === null) return undefined;
      const { d: aktuell, kopf, basis } = stand;
      const alt = basis.kopf;
      const patch: Record<string, unknown> = {};
      if (kopf.title !== alt.title) patch.title = kopf.title;
      if (kopf.descriptionMarkdown !== alt.descriptionMarkdown) patch.descriptionMarkdown = kopf.descriptionMarkdown === "" ? null : kopf.descriptionMarkdown;
      if (kopf.assigneeUserId !== alt.assigneeUserId) patch.assigneeUserId = kopf.assigneeUserId === "" ? null : kopf.assigneeUserId;
      if (kopf.columnId !== alt.columnId) patch.columnId = kopf.columnId;
      if (kopf.resubmitDate !== alt.resubmitDate) patch.resubmitDate = kopf.resubmitDate === "" ? null : kopf.resubmitDate;
      try {
        // Die Startversion der Bearbeitung, nicht die des zuletzt gesehenen Details (Review 15).
        return await p.client.updateTicket(aktuell.id, basis.version, patch);
      } catch (e) {
        // Den Serverstand sofort nachholen, die eigenen Eingaben bleiben (Review-Fokus 2).
        if (e instanceof ApiError && e.status === 409) {
          try { uebernehme(await holen(), "behalten"); } catch { /* der Hinweis bietet „Neu laden“ */ }
        }
        throw e;
      }
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      uebernehme(await holen(), behalten ? "behalten" : "ersetzen");
    },
    bekannteFelder: KOPF_FELDER,
    beschriftungen: { title: "Titel", descriptionMarkdown: "Beschreibung", assigneeUserId: "Zuständig",
                      columnId: "Status", resubmitDate: "Wiedervorlage" },
  });
  const gesperrt = formular.zustand !== "bereit";

  async function speichern(): Promise<void> {
    const ergebnis = await formular.aktion();
    if (ergebnis !== undefined) p.onSchliessen(true);
  }

  async function abbrechen(): Promise<void> {
    if (formular.laeuft) return;
    if (!(await darfAbbrechen(ungespeichert))) return;
    p.onSchliessen(geaendertSeitOeffnen);
  }

  const teileNeu = useCallback((neu: TicketDetail) => {
    uebernehme(neu, "teile");
    setGeaendertSeitOeffnen(true);
  }, [uebernehme]);

  if (d === null || kopf === null) {
    return (
      <Arbeitsdialog titel="Ticket" pfad={<span>{pfad}</span>} onAbbrechen={() => p.onSchliessen(false)}>
        {ladefehler !== null ? <Hinweis art="fehler">{ladefehler}</Hinweis> : <p>Lädt …</p>}
      </Arbeitsdialog>
    );
  }

  const spalte = spalten.find((s) => s.id === d.columnId);
  const marke = datumsMarke(d.resubmitDate, d.heute);
  const chipText = `${spalte?.name ?? "…"} · ${marke.text}`;
  const fehlerVon = (f: string): { fehler?: string } => {
    const text = formular.feldFehler[f];
    return text === undefined ? {} : { fehler: text };
  };
  const schnell = (k: Schnellknopf) => aendereKopf({
    resubmitDate: schnellknopfDatum(k, kopf.resubmitDate === "" ? null : kopf.resubmitDate, d.heute, feiertage) ?? "" });
  // Ein Befund an einem Feld im Steckbrief klappt ihn auf, sonst stünde der Fehler unsichtbar da.
  const kopfFehler = ["assigneeUserId", "columnId", "resubmitDate"].some((f) => formular.feldFehler[f] !== undefined);
  const zustaendig = leute.find((u) => u.id === kopf.assigneeUserId)
    ?? (d.assignee !== null && d.assignee.id === kopf.assigneeUserId ? d.assignee : null);
  const auswahlLeute = d.assignee !== null && !leute.some((u) => u.id === d.assignee!.id) ? [d.assignee, ...leute] : leute;

  const reiterListe = [
    { id: "verlauf", label: "Verlauf" }, { id: "teilaufgaben", label: "Teilaufgaben" },
    { id: "kunden", label: "Kunden und Bezüge" }, { id: "anhaenge", label: "Anhänge" },
    ...(p.zusatzReiter ?? []).map((r) => ({ id: r.id, label: r.label })),
  ];

  const wer = formular.fremderStand?.changedBy;
  const fremderHinweis = formular.zustand !== "fremder-stand" ? undefined : (
    <>
      {`Das Ticket wurde inzwischen geändert${wer !== undefined && wer !== "" ? ` (von ${wer})` : ""}. `
        + "Deine Eingaben stehen noch da — „Neu laden“ übernimmt den aktuellen Stand, "
        + "„Meine Eingaben behalten“ legt sie auf den neuen Stand."}
      <KiKnopf art="leise" onClick={() => { eingabenBehalten.current = true; void formular.neuLaden(); }}>
        Meine Eingaben behalten
      </KiKnopf>
    </>
  );

  return (
    <Arbeitsdialog titel={`${d.ticketNumber} ${d.title}`} pfad={<span>{pfad}</span>}
      onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={formular.laeuft}
      steckbrief={(
        <Steckbrief art="dialog" className="td__steckbrief" titel={`${d.ticketNumber} ${d.title}`}
          eingeklappt={kopfZu && !kopfFehler} onEingeklappt={(e) => setKopfZu(e.eingeklappt)}
          icon={<Icon name={ART[d.kind].symbol} label={ART[d.kind].name} />}
          status={marke.art === null ? <Chip>{chipText}</Chip> : <Chip statusArt={marke.art}>{chipText}</Chip>}
          werte={(
            <dl>
              <dt>Art</dt>
              <dd className="td__art"><Icon name={ART[d.kind].symbol} label="" /> {ART[d.kind].name}</dd>
              <dt>Unternehmen</dt>
              <dd><Chip {...hausfarbeChip(p.companies, d.companyShortCode)}>{d.companyShortCode}</Chip> {d.companyName}</dd>
              <dt>Kunden</dt><dd>{d.customers.length === 0 ? "ohne Kunde (intern)" : d.customers.map((c) => c.name).join(", ")}</dd>
              <dt>Ansprechpartner</dt><dd>{d.teile.contacts.length === 0 ? "–" : d.teile.contacts.map((c) => c.name).join(", ")}</dd>
              <dt>Erstellt</dt><dd>{`${d.createdBy?.name ?? "–"}, ${heuteBeschriftung(berlinerTag(d.createdAt))}`}</dd>
              <dt>Zuständig</dt>
              <dd data-frei className="td__zustaendig">
                <Avatar ohneName person={zustaendig} ich={zustaendig !== null && zustaendig.id === p.ich.id} />
                <Auswahl kompakt beschriftung="Zuständig" wert={kopf.assigneeUserId} gesperrt={gesperrt}
                  {...fehlerVon("assigneeUserId")}
                  onGeaendert={(e) => { formular.feldFehlerRaeumen("assigneeUserId"); aendereKopf({ assigneeUserId: e.wert }); }}>
                  <option value="">niemand</option>
                  {auswahlLeute.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Auswahl>
              </dd>
              <dt>Status</dt>
              <dd data-frei>
                <Auswahl kompakt beschriftung="Status" wert={kopf.columnId} gesperrt={gesperrt}
                  {...fehlerVon("columnId")}
                  onGeaendert={(e) => { formular.feldFehlerRaeumen("columnId"); aendereKopf({ columnId: e.wert }); }}>
                  {spalten.length === 0 && <option value={d.columnId}>…</option>}
                  {spalten.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Auswahl>
              </dd>
              {/* Eine Beschriftung (der Kennwert), das Feld trägt den Namen nur für die Vorlesestimme;
                  die Schnellknöpfe in einer Zeile darunter (Sichtprobe 8). */}
              <dt className="td__neuzeile">Wiedervorlage</dt>
              <dd data-frei className="td__wiedervorlage">
                <Eingabe art="datum" aria-label="Wiedervorlage" className="td__datum" id="td-wiedervorlage" name="td-wiedervorlage"
                  wert={kopf.resubmitDate} {...(gesperrt ? { gesperrt: true } : {})}
                  {...(formular.feldFehler.resubmitDate !== undefined ? { fehler: formular.feldFehler.resubmitDate } : {})}
                  onGeaendert={(e) => { formular.feldFehlerRaeumen("resubmitDate"); aendereKopf({ resubmitDate: e.wert }); }} />
                <span className="td__schnell">
                  <KiKnopf art="leise" className="td__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("TAG")}>+1 Tag</KiKnopf>
                  <KiKnopf art="leise" className="td__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("WOCHE")}>+1 Woche</KiKnopf>
                  <KiKnopf art="leise" className="td__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("MONAT")}>+1 Monat</KiKnopf>
                  <KiKnopf art="leise" className="td__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("KEINE")}>keine</KiKnopf>
                </span>
                <span className="td__datumhinweis">
                  {kopf.resubmitDate === "" ? "ohne Wiedervorlage: steht oben in „Jetzt“" : (
                    <>
                      <span>{heuteBeschriftung(kopf.resubmitDate)}</span>
                      {istFreierTag(kopf.resubmitDate, feiertage) ? " · Wochenende/Feiertag nur Hinweis" : ""}
                    </>
                  )}
                </span>
              </dd>
            </dl>
          )} />
      )}
      register={(
        <Register className="td__register" klebend aria-label="Bereiche des Tickets" aktiv={`td-${reiter}`}
          onReiterGewaehlt={(e) => setReiter(e.id.replace(/^td-/, ""))}>
          {reiterListe.map((r) => <Reiter key={r.id} fuer={`td-${r.id}`} beschriftung={r.label} />)}
        </Register>
      )}
      // Der Titel gehört zum Kopf über den Reitern (Sichtprobe 7): Slot `meldungen` steht zwischen
      // Steckbrief und Register, mit ihm der fremde Stand und die Meldungen des Kopfs.
      meldungen={(
        <div className="td__kopffeld">
          <Formular beschriftung="oben" {...formular.formularProps} hinweis={fremderHinweis}>
            <Feld id="td-titel" label="Titel" wert={kopf.title} onChange={(w) => aendereKopf({ title: w })}
              bearbeitbar pflicht max={200} fehler={formular.feldFehler.title} />
          </Formular>
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          {formular.zustand === "bereit" && stand?.basis.version !== d.version && geaendert && (
            <Hinweis art="warnung">
              Das Ticket wurde inzwischen geändert. Deine Änderungen am Kopf beziehen sich auf den früheren
              Stand — beim Speichern zeigt sich, ob sie sich vertragen.
            </Hinweis>
          )}
        </div>
      )}
      hinweis={<span>Kommentare, Teilaufgaben, Kunden, Bezüge und Anhänge sind sofort gespeichert.</span>}
      fuss={(
        <>
          <KiKnopf art="primaer" deaktiviert={gesperrt || !geaendert} onClick={() => { void speichern(); }}>Speichern</KiKnopf>
          <KiKnopf deaktiviert={formular.laeuft} onClick={() => { void abbrechen(); }}>Abbrechen</KiKnopf>
        </>
      )}>
      <div role="tabpanel" className="td__tafel" id={`td-${reiter}`} aria-label={reiterListe.find((r) => r.id === reiter)?.label}>
        {reiter === "verlauf" && (
          <VerlaufReiter d={d} client={p.client} ich={p.ich} onNeu={teileNeu} nachladen={nachladen}
            beschreibung={kopf.descriptionMarkdown} beschreibungGesperrt={gesperrt}
            beschreibungFehler={formular.feldFehler.descriptionMarkdown}
            onBeschreibung={(w) => { formular.feldFehlerRaeumen("descriptionMarkdown"); aendereKopf({ descriptionMarkdown: w }); }}
            entwurf={entwuerfe.kommentar} onEntwurf={(w) => setEntwuerfe((e) => ({ ...e, kommentar: w }))} />
        )}
        {reiter === "teilaufgaben" && (
          <TeilaufgabenReiter d={d} client={p.client} onNeu={teileNeu} nachladen={nachladen}
            entwurf={entwuerfe.teilaufgabe} onEntwurf={(w) => setEntwuerfe((e) => ({ ...e, teilaufgabe: w }))} />
        )}
        {reiter === "kunden" && (
          <KundenReiter d={d} client={p.client} companies={p.companies} onNeu={teileNeu} nachladen={nachladen}
            onZurAkte={p.onZurAkte} />
        )}
        {reiter === "anhaenge" && <AnhaengeReiter d={d} client={p.client} onNeu={teileNeu} nachladen={nachladen} />}
        {(p.zusatzReiter ?? []).filter((r) => r.id === reiter).map((r) => (
          <div key={r.id}>{r.inhalt(d, teileNeu, nachladen)}</div>
        ))}
      </div>
    </Arbeitsdialog>
  );
}
